import { forwardRef, useId } from 'react';

import { cx } from 'utils';

/**
 * Select — native dropdown styled to match `Input`.
 *
 * forwardRef so it plugs straight into React Hook Form's `register()`.
 * Pass `options` as `[{ value, label }]` (or strings) for convenience, or
 * compose `<option>` children directly.
 *
 * @param {string}  [label]
 * @param {string}  [hint]
 * @param {string}  [error]
 * @param {string}  [placeholder]   Rendered as a disabled first option.
 * @param {Array}   [options]       `[{ value, label }]` or string[].
 * @param {'sm'|'md'|'lg'} [size='md']
 */
const SIZES = {
  sm: 'h-9 text-sm rounded-lg',
  md: 'h-11 text-sm rounded-xl',
  lg: 'h-12 text-base rounded-xl',
};

const Select = forwardRef(function Select(
  {
    label,
    hint,
    error,
    placeholder,
    options,
    size = 'md',
    className = '',
    containerClassName = '',
    id,
    children,
    ...props
  },
  ref
) {
  const autoId = useId();
  const selectId = id || autoId;
  const describedBy = error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined;

  const normalized = (options || []).map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o
  );

  const field = cx(
    'w-full appearance-none bg-white text-slate-900',
    // slate-300 not slate-200 — see Input: a field needs a visible boundary.
    'border border-slate-300 transition-all duration-200 ease-premium',
    'hover:border-slate-400',
    'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25',
    'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-60',
    'pl-4 pr-10',
    SIZES[size] || SIZES.md,
    error && 'border-danger focus:border-danger focus:ring-danger/25',
    className
  );

  return (
    <div className={cx('w-full', containerClassName)}>
      {label && (
        <label htmlFor={selectId} className="mb-2 block text-sm font-semibold text-slate-800">
          {label}
        </label>
      )}

      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={field}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {normalized.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          {children}
        </select>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>

      {error ? (
        <p id={`${selectId}-error`} className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${selectId}-hint`} className="mt-1.5 text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

export default Select;
